import { type HistoryEntry, visitSpan } from "@patients/historyEntries";
import {
  Button,
  formatClockTime,
  TimeLabel,
} from "@scribenote/brian-design-system";
import { ViewRenderer } from "@views/ViewRenderer";
import type { View } from "@views/viewsClient";
import { useReviewView } from "@views/viewsQueries";
import { VisitProgressControl } from "@visits/VisitProgressControl";
import type { TodayVisit } from "@visits/visitsClient";
import type { ReactNode } from "react";

const dayFormat = new Intl.DateTimeFormat("en", {
  weekday: "short",
  month: "short",
  day: "numeric",
});

const day = (iso: string) => dayFormat.format(new Date(iso)).replace(",", "");
const clock = (iso: string | null) =>
  iso ? formatClockTime(new Date(iso)) : null;

const LABEL =
  "font-mono text-fine-print uppercase tracking-[0.14em] text-ink-body";

const visitMeta = (
  visit: TodayVisit,
  roomName: (id: string | null) => string | null,
): string => {
  const { start, end } = visitSpan(visit);
  const times = start
    ? [clock(start), clock(end)].filter(Boolean).join("–")
    : null;
  return [times, visit.assignedUserName, roomName(visit.currentRoomUuid)]
    .filter(Boolean)
    .join(" · ");
};

const Sheet = ({
  title,
  meta,
  action,
  children,
}: {
  title: string;
  meta: string;
  action?: ReactNode;
  children: ReactNode;
}) => (
  <article className="flex min-h-0 min-w-0 flex-col rounded-md border border-rule bg-card px-7 pb-[18px] pt-5 shadow-sheet">
    <header className="flex shrink-0 items-center justify-between gap-4 border-b-[1.5px] border-ink pb-2.5">
      <div className="min-w-0">
        <h2
          className="truncate font-display text-xl font-medium text-ink"
          data-dd-privacy="hidden"
        >
          {title}
        </h2>
        {meta && (
          <p
            className="mt-[3px] truncate text-mono-time text-xs text-annotation"
            data-dd-privacy="hidden"
          >
            {meta}
          </p>
        )}
      </div>
      {action}
    </header>
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="py-3">{children}</div>
    </div>
  </article>
);

const NoteSheet = ({
  title,
  meta,
  note,
}: {
  title: string;
  meta: string;
  note: View;
}) => {
  const review = useReviewView();
  const verified = note.reviewedAt
    ? `verified${note.reviewedByName ? ` by ${note.reviewedByName}` : ""} ${day(note.reviewedAt)}`
    : null;
  return (
    <Sheet
      title={title}
      meta={[meta, verified].filter(Boolean).join(" · ")}
      action={
        note.reviewedAt || !note.visitStageId ? null : (
          <Button
            variant="primary"
            size="sm"
            busy={review.isPending}
            onClick={() => review.mutate(note.id)}
          >
            Verify
          </Button>
        )
      }
    >
      <ViewRenderer view={note} frameless />
    </Sheet>
  );
};

const VisitSheet = ({
  visit,
  roomName,
}: {
  visit: TodayVisit;
  roomName: (id: string | null) => string | null;
}) => {
  return (
    <Sheet
      title={`${visit.reason ?? visit.visitType} · ${day(visit.scheduledStart ?? visit.createdAt)}`}
      meta={visitMeta(visit, roomName)}
    >
      <dl data-dd-privacy="hidden">
        <Row label="Visit status">
          <VisitProgressControl visit={visit} />
        </Row>
        {visit.reason && <Row label="Type">{visit.visitType}</Row>}
        {visit.cancelledAt && (
          <Row label="Cancelled">
            <TimeLabel value={visit.cancelledAt} className="text-ink" />
          </Row>
        )}
        {visit.stages.length > 0 && (
          <Row label="Stages">
            <ol className="flex flex-col gap-1">
              {visit.stages.map((stage) => (
                <li
                  key={stage.id}
                  className="flex items-baseline justify-between gap-4"
                >
                  <span
                    className={
                      stage.status === "pending"
                        ? "text-annotation"
                        : stage.status === "active"
                          ? "font-semibold"
                          : undefined
                    }
                  >
                    {stage.name}
                  </span>
                  {stage.enteredAt && <TimeLabel value={stage.enteredAt} />}
                </li>
              ))}
            </ol>
          </Row>
        )}
      </dl>
    </Sheet>
  );
};

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="grid grid-cols-[130px_minmax(0,1fr)] gap-4 border-b border-dotted border-rule-strong py-[11px]">
    <dt className={LABEL}>{label}</dt>
    <dd className="text-[0.96875rem] leading-[1.45] text-ink">{children}</dd>
  </div>
);

export const HistoryDetail = ({
  entry,
  roomName,
}: {
  entry: HistoryEntry;
  roomName: (id: string | null) => string | null;
}) => {
  if (entry.type === "view")
    return (
      <NoteSheet
        key={entry.id}
        title={`${entry.view.name} · ${day(entry.view.createdAt)}`}
        meta={[entry.view.subtitle, `created ${clock(entry.view.createdAt)}`]
          .filter(Boolean)
          .join(" · ")}
        note={entry.view}
      />
    );
  if (entry.note)
    return (
      <NoteSheet
        key={entry.id}
        title={`${entry.note.name} · ${day(entry.at)}`}
        meta={visitMeta(entry.visit, roomName)}
        note={entry.note}
      />
    );
  return <VisitSheet key={entry.id} visit={entry.visit} roomName={roomName} />;
};
